import React, { FC, ReactElement, memo, useCallback, useMemo, useState } from 'react';
import { Form } from 'antd';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { Modal } from 'shared/ui/Modal';
import { Select } from 'shared/ui/Select';
import { Checkbox } from 'shared/ui/Checkbox';
import { DatePicker } from 'shared/ui/DatePicker';
import {
  useCloseContractMutation,
  useCloseContractPreviewQuery,
  CloseContractParams,
  CloseContractPreviewParams,
  CloseContractRefundOption,
  Contract,
  ClosingWithoutVerificationReason,
} from 'entities/Contract';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { getServerDateFormat } from 'shared/utils/helpers/getDateFormat';
import { UserPermissions } from 'entities/User';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { EmailType } from 'entities/Email';
import { WarnTag } from 'shared/ui/WarnTag';
import { VerificationDocument } from 'entities/VerificationPhoto';
import { useDebouncedValue } from 'shared/utils/hooks/useDebouncedValue';
import { getCloseContractDisabledDate, getDefaultCloseDate } from '../utils/getCloseContractRestrictions';
import { CloseContractPreview } from './CloseContractPreview';

const PREVIEW_DEBOUNCE_MS = 400;

interface CloseContractProps {
  contract: Contract;
  action: ReactElement;
  isCloseBlocked: boolean;
  moveOutDocs?: VerificationDocument[];
}

export const CloseContract: FC<CloseContractProps> = memo((props) => {
  const { contract, action, isCloseBlocked, moveOutDocs } = props;

  const [form] = Form.useForm();

  const [showExtendedOptions, setShowExtendedOptions] = useState(false);
  const [isOpenedModal, setOpenedModal] = useState(false);

  const isForbidden = useCheckPermission(UserPermissions.CLOSE_CONTRACT_EXTENDED);
  const hasExtendedClosePermission = !isForbidden;

  const { t } = useAppTranslation(['contracts', 'common']);

  const { isDesktop } = useScreenBreakpoints();

  const [closeContract, { isLoading }] = useCloseContractMutation();

  const defaultCloseDate = useMemo(() => getDefaultCloseDate(contract, hasExtendedClosePermission), [contract, hasExtendedClosePermission]);

  const openModal = useCallback((): void => {
    form.setFieldsValue({
      leaveDate: defaultCloseDate,
    });
    setOpenedModal(true);
  }, [defaultCloseDate, form]);

  const refundOptions = useMemo(
    () => [
      { label: t('Default'), value: 'default' },
      { label: t('Refund all'), value: 'refund-all' },
      { label: t('Disable refunds'), value: 'no-refunds' },
    ],
    [t],
  );

  const emailTypeOptions = useMemo(() => {
    const options = [
      { label: t('Default'), value: EmailType.CLOSE_CONTRACT },
      { label: t('Debtor email'), value: EmailType.DEBTOR_CLOSE_CONTRACT },
    ];

    if (!isForbidden) {
      options.push({ label: t('Extraordinary termination email'), value: EmailType.EXTRAORDINARY_CLOSE_CONTRACT });
    }

    return options;
  }, [isForbidden, t]);

  const closingWithoutVerificationReasonOptions = Object.values(ClosingWithoutVerificationReason).map((option) => ({
    label: t(option),
    value: option,
  }));

  const defaultValues = useMemo(
    () => ({
      refundOptions: 'default',
      leaveDate: defaultCloseDate,
      ignoreMinContractDuration: false,
      enableDepositCharge: true,
      enableUserBalanceCharge: false,
      issueUnpaidInvoices: true,
      emailType: EmailType.CLOSE_CONTRACT,
    }),
    [defaultCloseDate],
  );

  const leaveDateValue = Form.useWatch('leaveDate', form);
  const refundOptionValue = Form.useWatch<CloseContractRefundOption>('refundOptions', form);
  const issueUnpaidInvoicesValue = Form.useWatch<boolean>('issueUnpaidInvoices', form);
  const enableDepositChargeValue = Form.useWatch<boolean>('enableDepositCharge', form);
  const enableUserBalanceChargeValue = Form.useWatch<boolean>('enableUserBalanceCharge', form);
  const ignoreMinContractDurationValue = Form.useWatch<boolean>('ignoreMinContractDuration', form);

  const previewParams = useMemo<CloseContractPreviewParams>(
    () => ({
      leaveDate: getServerDateFormat(leaveDateValue ?? defaultCloseDate),
      refundOptions: refundOptionValue ?? (defaultValues.refundOptions as CloseContractRefundOption),
      issueUnpaidInvoices: issueUnpaidInvoicesValue ?? defaultValues.issueUnpaidInvoices,
      enableDepositCharge: enableDepositChargeValue ?? defaultValues.enableDepositCharge,
      enableUserBalanceCharge: enableUserBalanceChargeValue ?? defaultValues.enableUserBalanceCharge,
      ignoreMinContractDuration: ignoreMinContractDurationValue ?? defaultValues.ignoreMinContractDuration,
    }),
    [
      leaveDateValue,
      defaultCloseDate,
      refundOptionValue,
      issueUnpaidInvoicesValue,
      enableDepositChargeValue,
      enableUserBalanceChargeValue,
      ignoreMinContractDurationValue,
      defaultValues,
    ],
  );

  const debouncedPreviewParams = useDebouncedValue(previewParams, PREVIEW_DEBOUNCE_MS);

  const {
    data: preview,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
    error: previewError,
    refetch: refetchPreview,
  } = useCloseContractPreviewQuery({ contractId: contract.contractId, params: debouncedPreviewParams }, { skip: !isOpenedModal });

  const previewErrorMessage = useMemo(() => {
    const message = (previewError as CustomAny)?.data?.message;

    return Array.isArray(message) ? message.join('; ') : message;
  }, [previewError]);

  // params changed but the debounced request has not been sent yet - the shown numbers are already stale
  const isPreviewPending = isPreviewFetching || previewParams !== debouncedPreviewParams;

  const disableDate = useCallback(getCloseContractDisabledDate(contract, hasExtendedClosePermission), [
    contract,
    hasExtendedClosePermission,
  ]);

  const closeBlockWarning = hasExtendedClosePermission
    ? t('Closing the contract is not possible until all move-out photos are verified!', { ns: 'contracts' })
    : t('Closing the contract is not possible until all move-out photos are approved!', { ns: 'contracts' });

  const toggleExtendedOptions = (): void => {
    setShowExtendedOptions((prev) => !prev);
  };

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
    form.resetFields();
  }, [form]);

  const handleCloseContract = useCallback(
    async (data: CloseContractParams): Promise<void> => {
      try {
        if (contract) {
          await closeContract({
            contractId: contract.contractId,
            params: {
              ...data,
              leaveDate: getServerDateFormat(data.leaveDate),
            },
          }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Contract closed successfully', { ns: 'common' }));
          closeModal();
        }
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when closing contract', { ns: 'common' }));
        console.log('error', error);
      } finally {
        closeModal();
      }
    },
    [contract, closeContract, t, closeModal],
  );

  return (
    <>
      {React.cloneElement(action, { onClick: openModal })}

      <Modal width={isDesktop ? 848 : 350} isOpen={isOpenedModal} onClose={closeModal}>
        <div className="flex flex-col desktop:flex-row gap-6">
          <Form
            form={form}
            layout="vertical"
            initialValues={defaultValues}
            onFinish={handleCloseContract}
            className="flex flex-col flex-1 min-w-0"
          >
            <div className="font-semibold text-xl mb-3">{t('Close contract options')}</div>
            <div className="text-primaryLight font-normal">{t('Are you sure you want to close this contract?')}</div>

            {isCloseBlocked && <WarnTag textSize="medium">{closeBlockWarning}</WarnTag>}

            <div className="mt-5">
              <Form.Item
                className="w-full"
                label={t('Close date')}
                name="leaveDate"
                rules={[
                  {
                    required: true,
                    message: t('Please enter leave date!'),
                  },
                ]}
              >
                <DatePicker
                  className="h-[52px] w-full"
                  format={CLIENT_DATE_FORMAT}
                  placeholder={t('Enter leave date')}
                  disabledDate={disableDate}
                  // inputReadOnly
                  defaultPickerValue={defaultCloseDate}
                />
              </Form.Item>

              {!moveOutDocs?.length && (
                <Form.Item
                  label={t('Closing without verification reason')}
                  name="closingWithoutVerificationReason"
                  className="flex-1"
                  rules={[
                    {
                      required: true,
                      message: t('Please select reason!'),
                    },
                  ]}
                >
                  <Select options={closingWithoutVerificationReasonOptions} placeholder={t('Please, select reason')} bordered />
                </Form.Item>
              )}

              <Form.Item
                label={t('Email type')}
                name="emailType"
                className="flex-1"
                rules={[
                  {
                    required: true,
                    message: t('Please select email type!'),
                  },
                ]}
              >
                <Select options={emailTypeOptions} placeholder={t('Please, select email type')} bordered />
              </Form.Item>
            </div>

            <div className="mb-5">
              <Form.Item label={t('Refund options')} name="refundOptions" className="flex-1">
                <Select options={refundOptions} placeholder={t('Please, select warehouse')} bordered disabled={isForbidden} />
              </Form.Item>
              <div className={showExtendedOptions ? undefined : 'hidden'}>
                <Form.Item name="issueUnpaidInvoices" valuePropName="checked" className="mb-3">
                  <Checkbox name="issueUnpaidInvoices" disabled={isForbidden}>
                    {t('Issue unpaid invoices')}
                  </Checkbox>
                </Form.Item>
                <Form.Item name="enableDepositCharge" valuePropName="checked" className="mb-3">
                  <Checkbox name="enableDepositCharge" disabled={isForbidden}>
                    {t('Enable deposit charge')}
                  </Checkbox>
                </Form.Item>
                <Form.Item name="enableUserBalanceCharge" valuePropName="checked" className="mb-3">
                  <Checkbox name="enableUserBalanceCharge" disabled={isForbidden}>
                    {t('Enable user balance charge')}
                  </Checkbox>
                </Form.Item>
                <Form.Item name="ignoreMinContractDuration" valuePropName="checked" className="mb-3">
                  <Checkbox name="ignoreMinContractDuration" disabled={isForbidden}>
                    {t('Ignore minimum rental period')}
                  </Checkbox>
                </Form.Item>
              </div>
              <div className="flex justify-center">
                <Button theme="clear" onClick={toggleExtendedOptions} isDisabled={isForbidden}>
                  {showExtendedOptions ? t('Hide extended options') : t('Show extended options')}
                </Button>
              </div>
            </div>

            {/* mt-auto keeps the actions on the modal floor when the preview column is the taller one */}
            <div className="flex justify-end gap-3 mt-auto pt-2">
              <Button theme="secondary" onClick={closeModal}>
                {t('Cancel', { ns: 'common' })}
              </Button>

              {/* a failed preview must not block closing: the preview has failure modes the close itself does not */}
              <Button type="submit" isLoading={isLoading} isDisabled={isCloseBlocked || isPreviewPending}>
                {t('Close contract')}
              </Button>
            </div>
          </Form>

          <div className="desktop:w-[360px] desktop:shrink-0 desktop:border-l desktop:border-t-0 desktop:pl-6 desktop:pt-0 border-t border-primary pt-4">
            <CloseContractPreview
              preview={preview}
              isFetching={isPreviewPending}
              isError={isPreviewError}
              errorMessage={previewErrorMessage}
              onRetry={refetchPreview}
            />
          </div>
        </div>
      </Modal>
    </>
  );
});
